// ═══════════════════════════════════════════════════════════════
// mailingApi.js — Client /api/mailing/* (Session J)
// ═══════════════════════════════════════════════════════════════
// Pattern emprunté à familyApi.js / bookApi.js.

const getToken = () => localStorage.getItem('oracle_token');

export class MailingApiError extends Error {
    constructor(status, code, message, details) {
        super(message || code || `HTTP ${status}`);
        this.name = 'MailingApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new MailingApiError(
                res.status,
                data.code || data.error,
                data.error || data.message || `HTTP ${res.status}`,
                data.details
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

// ─── Templates ────────────────────────────────────────────────────
export const listTemplates = () => request('/api/mailing/templates');
export const previewTemplateUrl = (slug) =>
    `/api/mailing/templates/${encodeURIComponent(slug)}/preview`;

// ─── Quotas ───────────────────────────────────────────────────────
export const getQuotas = () => request('/api/mailing/quotas');

// ─── Listes ───────────────────────────────────────────────────────
export const listLists = () => request('/api/mailing/lists');

export const createList = (payload) =>
    request('/api/mailing/lists', { method: 'POST', body: payload });

export const deleteList = (listmonkListId) =>
    request(`/api/mailing/lists/${listmonkListId}`, { method: 'DELETE' });

export const updateList = (listmonkListId, { name, description } = {}) =>
    request(`/api/mailing/lists/${listmonkListId}`, {
        method: 'PATCH',
        body: { name, description },
    });

// ─── Abonnés ──────────────────────────────────────────────────────
export const listSubscribers = (listmonkListId, { page = 1, perPage = 50, q = '', status = '' } = {}) => {
    const qs = new URLSearchParams({ page, perPage, q });
    if (status) qs.set('status', status);
    return request(`/api/mailing/lists/${listmonkListId}/subscribers?${qs}`);
};

// Bulk action : action = 'remove' (soft, retire de la liste) | 'delete' (hard, DELETE listmonk)
export const bulkSubscriberAction = (listmonkListId, { emails, action }) =>
    request(`/api/mailing/lists/${listmonkListId}/subscribers/bulk-action`, {
        method: 'POST',
        body: { emails, action },
        timeoutMs: 120000,
    });

export const addSubscriber = (listmonkListId, { email, name = null, attribs = {}, skipDoubleOptin = false }) =>
    request(`/api/mailing/lists/${listmonkListId}/subscribers`, {
        method: 'POST',
        body: { email, name, attribs, skipDoubleOptin },
    });

export const removeSubscriber = (listmonkListId, email) =>
    request(`/api/mailing/lists/${listmonkListId}/subscribers/${encodeURIComponent(email)}`, {
        method: 'DELETE',
    });

// Export CSV — fetch + blob + déclenche download navigateur.
// Renvoie { filename, total } pour feedback toast côté caller.
export async function exportSubscribersCSV(listmonkListId) {
    const res = await fetch(`/api/mailing/lists/${listmonkListId}/subscribers/export`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new MailingApiError(res.status, data.code, data.error || `HTTP ${res.status}`, data.details);
    }
    // Extract filename from Content-Disposition
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^";]+)"?/);
    const filename = m ? m[1] : `mailing-export-${Date.now()}.csv`;

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    return { filename };
}

// Bulk import — one-shot JSON (sans progression)
export const bulkImportSubscribers = (listmonkListId, payload) =>
    request(`/api/mailing/lists/${listmonkListId}/subscribers/bulk`, {
        method: 'POST',
        body: payload,
        timeoutMs: 600000, // 10min pour gros imports
    });

// Bulk import — mode SSE avec callbacks de progression.
// Pattern miroir studioImagesApi.js : fetch + ReadableStream + parser SSE manuel.
// Retourne un AbortController pour cleanup côté caller.
export function bulkImportSubscribersStream(listmonkListId, payload, { onProgress, onDone, onError } = {}) {
    const controller = new AbortController();
    (async () => {
        try {
            const res = await fetch(`/api/mailing/lists/${listmonkListId}/subscribers/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                    'Authorization': `Bearer ${getToken()}`,
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!res.ok && !res.headers.get('content-type')?.includes('text/event-stream')) {
                const data = await res.json().catch(() => ({}));
                onError?.({ code: data.code || 'http_error', error: data.error || `HTTP ${res.status}`, status: res.status });
                return;
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);

                    let eventName = 'message';
                    let dataStr = '';
                    for (const line of rawEvent.split('\n')) {
                        if (line.startsWith('event: ')) eventName = line.slice(7).trim();
                        else if (line.startsWith('data: ')) dataStr += line.slice(6);
                    }
                    if (!dataStr) continue;

                    let data;
                    try { data = JSON.parse(dataStr); } catch { continue; }

                    if (eventName === 'progress') onProgress?.(data);
                    else if (eventName === 'done') onDone?.(data);
                    else if (eventName === 'error') onError?.(data);
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                onError?.({ code: 'aborted', error: 'Import annulé' });
            } else {
                onError?.({ code: 'network', error: err.message || 'Erreur réseau' });
            }
        }
    })();
    return controller;
}

// ─── Campagnes ────────────────────────────────────────────────────
export const listCampaigns = ({ limit = 50, offset = 0 } = {}) =>
    request(`/api/mailing/campaigns?limit=${limit}&offset=${offset}`);

export const getCampaign = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}`);

export const createCampaign = (payload) =>
    request('/api/mailing/campaigns', { method: 'POST', body: payload });

export const sendTest = (campaignId, toEmail) =>
    request(`/api/mailing/campaigns/${campaignId}/test`, {
        method: 'POST',
        body: { toEmail },
    });

export const sendCampaign = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}/send`, { method: 'POST' });

export const scheduleCampaign = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}/schedule`, { method: 'POST' });

export const cancelCampaign = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}/cancel`, { method: 'POST' });

export const deleteCampaign = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}`, { method: 'DELETE' });

export const listBulkImports = (listId, { limit = 20 } = {}) =>
    request(`/api/mailing/lists/${listId}/bulk-imports?limit=${limit}`);

export const refreshStats = (campaignId) =>
    request(`/api/mailing/campaigns/${campaignId}/stats`);

export const draftWithAi = (payload) =>
    request('/api/mailing/campaigns/draft-with-ai', {
        method: 'POST',
        body: payload,
        timeoutMs: 60000,
    });

// ─── Module par projet (admin) ────────────────────────────────────
export const activateForProject = (projectId, payload = {}) =>
    request(`/api/mailing/projects/${projectId}/activate`, {
        method: 'POST',
        body: payload,
    });

export const deactivateForProject = (projectId) =>
    request(`/api/mailing/projects/${projectId}/deactivate`, { method: 'POST' });

export const getProjectSettings = (projectId) =>
    request(`/api/mailing/projects/${projectId}/settings`);
